'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { Comparisons } from '@/constants/Comparison';
import Custom404 from '@/app/[locale]/[...not_found]/404';
import { HiMiniCheckBadge, HiOutlineArrowRight, HiOutlineTrophy } from 'react-icons/hi2';
import Image from 'next/image';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import ComparisonSummary from '@/components/blocks/ComparisonSummary';
import CTABanner from '@/components/blocks/CTABanner';
import LouiseButton from '@/components/buttons/LouiseButton';
import SectionTrustedBy from '@/components/sectionTrustedBy/SectionTrustedBy';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

interface PageContentProps {
  locale: string;
  name: string;
}

export default function PageContent({ locale, name }: PageContentProps) {
  const { t } = useTranslation(locale);

  if (!name || !Comparisons.some((dr) => dr.pagePath == name)) {
    return <Custom404 locale={locale} />;
  }

  const item = Comparisons.find((dr) => dr.pagePath.toLowerCase() == name.toLowerCase());

  if (!item) {
    return <Custom404 locale={locale} />;
  }

  return (
    <div className="tw-bg-white tw-font-['Lexend']">
      {/* Hero Section */}
      <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Background decoration */}
        <div className="tw-absolute tw-top-0 tw-right-0 tw-w-96 tw-h-96 tw-bg-gradient-to-l tw-from-blue-100/30 tw-to-transparent tw-rounded-full tw-blur-3xl"></div>

        <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-6xl">
          <div className="tw-text-center tw-mb-12">
            {/* Breadcrumb */}
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-mb-6">
              <span>{t('Comparison/home')}</span>
              <HiOutlineArrowRight className="tw-w-4 tw-h-4" />
              <span>{t('Comparison/comparisons')}</span>
              <HiOutlineArrowRight className="tw-w-4 tw-h-4" />
              <span className="tw-text-blue-600 tw-font-semibold">{t(item.title)}</span>
            </div>

            {/* Badge */}
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-8">
              <HiOutlineTrophy className="tw-w-4 tw-h-4" />
              {t('Comparison/detailed-comparison')}
            </div>

            {/* Title */}
            <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight tw-max-w-4xl tw-mx-auto">
              {t(item.title)}
            </h1>

            {/* Subtitle */}
            <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-mb-12">
              {ReactHtmlParser(t(item.summary_short))}
            </p>

            {/* Hero Image */}
            <div className="tw-relative tw-max-w-4xl tw-mx-auto">
              <div className="tw-relative tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-2xl tw-border tw-border-gray-200">
                <Image
                  priority
                  quality={100}
                  className="tw-w-full tw-h-auto tw-rounded-2xl"
                  src={item.imageUrl}
                  alt={t(item.title)}
                  width={800}
                  height={450}
                />

                {/* Overlay badge */}
                <div className="tw-absolute tw-top-8 tw-left-8">
                  <div className="tw-bg-green-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-2">
                    <AiOutlineThunderbolt className="tw-w-4 tw-h-4" />
                    {t('Comparison/louisebot-advantage')}
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="tw-absolute tw--top-4 tw--right-4 tw-w-24 tw-h-24 tw-bg-gradient-to-br tw-from-purple-400 tw-to-pink-400 tw-rounded-full tw-opacity-20 tw-blur-xl"></div>
              <div className="tw-absolute tw--bottom-4 tw--left-4 tw-w-32 tw-h-32 tw-bg-gradient-to-br tw-from-blue-400 tw-to-cyan-400 tw-rounded-full tw-opacity-20 tw-blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="tw-py-20">
        <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-6xl">
          <div className="tw-space-y-20">
            {item.contents?.map((content, index) => (
              <article key={index} className="tw-group">
                {/* Section Header */}
                <div className="tw-text-center tw-mb-12">
                  <h2 className="tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
                    {t(content.header)}
                  </h2>
                </div>

                <div className="tw-grid lg:tw-grid-cols-2 tw-gap-12 tw-items-start">
                  {/* Content */}
                  <div className={`tw-space-y-8 ${index % 2 === 1 ? 'lg:tw-order-2' : ''}`}>
                    {content.body.map((data, i) => (
                      <div
                        key={i}
                        className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-p-8 tw-shadow-sm hover:tw-shadow-lg tw-transition-all tw-duration-300"
                      >
                        <div className="tw-flex tw-items-start tw-gap-4">
                          <div className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mt-1">
                            <HiMiniCheckBadge className="tw-w-5 tw-h-5 tw-text-green-600" />
                          </div>
                          <div className="tw-flex-grow">
                            <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-leading-tight">
                              {t(data.title)}
                            </h3>
                            <p className="tw-text-gray-600 tw-leading-relaxed">{t(data.description)}</p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Call to action */}
                    <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-purple-50 tw-border tw-border-blue-200 tw-rounded-2xl tw-p-8 tw-text-center">
                      <h4 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-4">
                        {t('Comparison/ready-to-experience')}
                      </h4>
                      <LouiseButton
                        color="blue"
                        cta={t('Comparison/try-louisebot')}
                        ctaTarget="register"
                        locale={locale}
                      />
                    </div>
                  </div>

                  {/* Image */}
                  <div className={`tw-relative ${index % 2 === 1 ? 'lg:tw-order-1' : ''}`}>
                    <div className="tw-relative tw-bg-white tw-p-4 tw-rounded-2xl tw-shadow-xl tw-border tw-border-gray-200 tw-transition-transform tw-duration-300 group-hover:tw-scale-[1.02]">
                      <Image
                        priority={index < 2}
                        quality={100}
                        className="tw-w-full tw-h-auto tw-rounded-xl"
                        src={content.imageUrl}
                        alt={content.header}
                        width={600}
                        height={400}
                      />

                      {/* Image overlay */}
                      <div className="tw-absolute tw-inset-4 tw-bg-gradient-to-t tw-from-black/20 tw-to-transparent tw-rounded-xl tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300"></div>
                    </div>

                    {/* Floating metric */}
                    {index === 0 ? (
                      <div className="tw-absolute tw--bottom-4 tw--right-4 tw-bg-white tw-rounded-2xl tw-p-4 tw-shadow-lg tw-border tw-border-gray-200">
                        <div className="tw-text-2xl tw-font-bold tw-text-blue-600 tw-mb-1">3x</div>
                        <div className="tw-text-xs tw-text-gray-600">{t('Comparison/faster')}</div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>

                {/* Divider */}
                {index < item.contents.length - 1 && (
                  <div className="tw-flex tw-items-center tw-justify-center tw-mt-20">
                    <div className="tw-w-24 tw-h-1 tw-bg-gradient-to-r tw-from-blue-300 tw-to-purple-300 tw-rounded-full"></div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Section */}
      {item && (
        <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-6xl">
          <ComparisonSummary
            summery={t(item.summary)}
            replaceText={t(item.contents[1]?.header || '')}
            locale={locale}
          />
        </div>
      )}

      {/* Trust Section */}
      <SectionTrustedBy locale={locale} />

      {/* CTA Section */}
      <CTABanner
        title={t('Home/ReadyToGetStarted/cta-title')}
        description={t('Home/ReadyToGetStarted/cta-description')}
        cta={t('Home/ReadyToGetStarted/start-trial-aria')}
        ctaTarget="register"
        locale={locale}
      />
    </div>
  );
}
